(() => {
  if (customElements.get('mermacorn-set-options')) return;

  class MermacornSetOptions extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.selects = Array.from(this.querySelectorAll('[data-color-choice]'));
      this.letter = this.querySelector('input[data-letter]');
      this.letterWarning = this.querySelector('[data-letter-warning]');
      this.storageKey = `skelody:mermacorn:choices:v2:${this.dataset.themeId}:${this.dataset.setKey}`;
      this.status = this.querySelector('[data-selection-status]');
      this.resetButton = this.querySelector('[data-reset]');
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (saved?.version === 2 && saved.selections && typeof saved.selections === 'object') {
          this.selects.forEach(select => {
            const value = saved.selections[select.dataset.braceletId];
            if (value && typeof value.color === 'string' && Array.from(select.options).some(option => option.value === value.color)) {
              select.value = value.color;
            }
            if (select.dataset.braceletId === this.letter?.dataset.letterBraceletId && typeof value?.letter === 'string' && this.normalizeLetter(value.letter)) {
              this.letter.value = this.normalizeLetter(value.letter);
            }
          });
        }
      } catch { /* Choices remain usable if tab storage is unavailable or invalid. */ }
      this.onLetterInput = () => {
        const normalized = this.normalizeLetter(this.letter.value);
        if (normalized) this.letter.value = normalized;
        const invalid = this.letter.value !== '' && !normalized;
        this.letter.setCustomValidity(invalid ? 'Please enter one capital letter from A–Z only.' : '');
        this.letterWarning.hidden = !invalid;
        this.save();
      };
      this.letter?.addEventListener('input', this.onLetterInput);
      this.onChange = event => {
        if (this.selects.includes(event.target)) this.save();
      };
      this.onReset = () => {
        this.selects.forEach(select => { select.value = select.dataset.defaultValue || ''; select.setCustomValidity?.(''); });
        if (this.letter) { this.letter.value = ''; this.letter.setCustomValidity(''); }
        if (this.letterWarning) this.letterWarning.hidden = true;
        if (this.quantity) {
          this.quantity.value = this.quantity.defaultValue;
          this.validateQuantity();
        }
        this.save();
      };
      this.form = this.querySelector('[data-cart-form]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.purchaseStatus = this.querySelector('[data-purchase-status]');
      this.quantity = this.querySelector('[data-quantity-1-to-99]');
      this.quantityError = this.querySelector('[data-quantity-error]');
      this.onQuantityInput = () => this.validateQuantity();
      this.quantity?.addEventListener('input', this.onQuantityInput);
      this.onSubmit = event => this.addToCart(event);
      this.form?.addEventListener('submit', this.onSubmit);
      this.setupGalleryTracking();
      this.addEventListener('change', this.onChange);
      if (this.resetButton) {
        this.resetButton.hidden = false;
        this.resetButton.addEventListener('click', this.onReset);
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.quantity?.removeEventListener('input', this.onQuantityInput);
      this.letter?.removeEventListener('input', this.onLetterInput);
      this.removeEventListener('change', this.onChange);
      this.resetButton?.removeEventListener('click', this.onReset);
      window.removeEventListener('scroll', this.onGalleryScroll);
      window.removeEventListener('resize', this.onGalleryScroll);
      this.removeEventListener('focusout', this.onGalleryScroll);
      this.galleryResizeObserver?.disconnect();
      cancelAnimationFrame(this.galleryFrame);
      this.galleryFrame = 0;
      this.connected = false;
    }

    setupGalleryTracking() {
      this.bracelets = Array.from(this.querySelectorAll('fieldset[data-photo-slot]'));
      const gallery = this.closest('.mc-set-detail')?.querySelector('.mc-set-detail__gallery');
      this.galleryPhotos = Array.from(gallery?.querySelectorAll('li[data-photo-slot]') || []);
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.onGalleryScroll = () => {
        if (this.galleryFrame) return;
        this.galleryFrame = requestAnimationFrame(() => {
          this.galleryFrame = 0;
          this.updateActiveBracelet();
        });
      };
      window.addEventListener('scroll', this.onGalleryScroll, { passive: true });
      window.addEventListener('resize', this.onGalleryScroll);
      this.addEventListener('focusout', this.onGalleryScroll);
      if ('ResizeObserver' in window && gallery) {
        this.galleryResizeObserver = new ResizeObserver(this.onGalleryScroll);
        this.galleryResizeObserver.observe(gallery);
        this.galleryResizeObserver.observe(this);
      }
      this.onGalleryScroll();
    }

    updateActiveBracelet() {
      let activeSlot = null;
      if (this.desktop.matches) {
        const top = parseFloat(getComputedStyle(this).top) || 0;
        const midpoint = top + Math.max(0, window.innerHeight - top) / 2;
        let closest = Infinity;
        this.galleryPhotos.forEach(photo => {
          const rect = photo.getBoundingClientRect();
          if (rect.bottom <= top || rect.top >= window.innerHeight) return;
          const distance = Math.abs((rect.top + rect.bottom) / 2 - midpoint);
          if (distance < closest) {
            closest = distance;
            activeSlot = photo.dataset.photoSlot;
          }
        });
      }
      let activeBracelet = null;
      this.bracelets.forEach(bracelet => {
        const active = bracelet.dataset.photoSlot === activeSlot;
        if (active) {
          bracelet.setAttribute('aria-current', 'true');
          activeBracelet = bracelet;
        } else {
          bracelet.removeAttribute('aria-current');
        }
      });
      if (!activeBracelet || this.contains(document.activeElement)) return;
      const panel = this.getBoundingClientRect();
      const field = activeBracelet.getBoundingClientRect();
      if (field.top < panel.top + 8) this.scrollTop += field.top - panel.top - 8;
      else if (field.bottom > panel.bottom - 8) this.scrollTop += field.bottom - panel.bottom + 8;
    }

    normalizeLetter(value) {
      return /^[A-Za-z]$/.test(value) ? value.toUpperCase() : '';
    }

    getSelection() {
      const bracelets = this.selects.map(select => ({
        id: select.dataset.braceletId,
        label: select.dataset.braceletLabel,
        color: Array.from(select.options).some(option => option.value === select.value) ? select.value : '',
        letter: select.dataset.braceletId === this.letter?.dataset.letterBraceletId ? this.normalizeLetter(this.letter.value) : ''
      }));
      const properties = {};
      bracelets.forEach((bracelet, index) => {
        if (bracelet.letter) properties[`Bracelet ${index + 1} letter`] = bracelet.letter;
        if (bracelet.color) properties[`Bracelet ${index + 1} bead color`] = bracelet.color;
      });
      return { mode: 'product', setKey: this.dataset.setKey, setName: this.dataset.setName, bracelets, properties };
    }

    validateQuantity(onSubmit = false) {
      if (!this.quantity) return true;
      const valid = /^(?:[1-9]|[1-9][0-9])$/.test(this.quantity.value);
      const showError = !valid && (onSubmit || this.quantity.value !== '');
      this.quantity.setCustomValidity(showError ? this.dataset.quantityErrorLabel : '');
      if (showError) this.quantity.setAttribute('aria-invalid', 'true');
      else this.quantity.removeAttribute('aria-invalid');
      if (this.quantityError) this.quantityError.hidden = !showError;
      return valid;
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.pending || this.dataset.cartReady !== 'true' || this.addButton.disabled) return;
      if (!this.validateQuantity(true)) { this.quantity.focus(); this.quantity.reportValidity(); return; }
      if (!this.form.reportValidity()) return;
      const selection = this.getSelection();
      if (selection.bracelets.length !== 4 || selection.bracelets.some(item => !item.color || /placeholder/i.test(item.color) || (item.id === this.letter?.dataset.letterBraceletId && !item.letter))) return;
      const id = this.form.elements.namedItem('id').value;
      if (!/^\d+$/.test(id)) return;
      const quantity = Number(this.quantity.value);
      this.save();
      this.pending = true;
      this.addButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.purchaseStatus.textContent = this.dataset.addingLabel;
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), item => item.dataset.sectionId).filter(Boolean))];
        const response = await fetch(`${this.form.action}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity, properties: selection.properties }], sections: sections.join(','), sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok || !Array.isArray(data.items) || data.items.length !== 1) throw new Error('cart');
        this.purchaseStatus.textContent = this.dataset.successLabel;
        this.cartLink.hidden = false;
        this.cartLink.focus();
        const cartUpdate = fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        }).then(async response => {
          if (!response.ok) throw new Error('Cart refresh failed');
          const cart = await response.json();
          return {
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, source: 'mermacorn-set-options', didError: false }
          };
        });
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: id, quantity }],
          promise: cartUpdate
        }));
        this.dispatchEvent(new CustomEvent('mermacorn:cart-added', { bubbles: true, detail: data }));
        await cartUpdate.catch(error => console.warn('[mermacorn] Cart refresh failed:', error));
      } catch {
        this.purchaseStatus.textContent = this.dataset.errorLabel;
        this.cartLink.hidden = false;
      } finally {
        this.pending = false;
        this.addButton.disabled = this.dataset.cartReady !== 'true';
        this.form.removeAttribute('aria-busy');
      }
    }

    save() {
      const selection = this.getSelection();
      let persisted = false;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          version: 2,
          selections: Object.fromEntries(selection.bracelets.map(bracelet => [bracelet.id, { color: bracelet.color, letter: bracelet.letter }]))
        }));
        persisted = true;
      } catch { /* Preserve selections when tab storage is unavailable. */ }
      if (this.status) this.status.textContent = persisted ? this.dataset.savedLabel : '';
      this.dispatchEvent(new CustomEvent('mermacorn:selection-change', { bubbles: true, detail: selection }));
    }
  }

  customElements.define('mermacorn-set-options', MermacornSetOptions);
})();





